import * as React from "react"
import {useEffect, useState} from "react"
import {getSubmit} from "@ui-seeker/utils"

import {SitesMultiSelect} from "@components"
import {BACKEND_APIS} from "@constants"
import {createBatchFormData} from "../../@signals"


export default function SitesAutocomplete({required, ...props}) {
	const [options, setOptions] = useState([])
	const customerID = createBatchFormData.value.customerID
	const markets = createBatchFormData.value.markets
	const batchType = createBatchFormData.value.batchType
	const isRaw2Sku = batchType === 'raw2sku'
	const isRaw2Raw = batchType === 'raw2raw'
	// war2war anchors on competitor listings, so this picker means the same thing it does
	// for raw2raw — the competitor sites — even though the roles inside the batch are
	// reversed. Its own-site picker is a separate component (OwnSitesAutocomplete).
	const isWar2War = batchType === 'war2war'
	const isCompetitorPicker = isRaw2Raw || isWar2War

	// raw2sku → products-review (market filter); raw2raw/war2war → competitor sites of the
	// customer; sku2raw → batch-admin sites (customer filter)
	const urlAjax = isRaw2Sku
		? '/api/products-review/sites'
		: isCompetitorPicker
			? `${BACKEND_APIS.BATCH_ADMIN}/competitor-sites`
			: `${BACKEND_APIS.BATCH_ADMIN}/sites`

	// raw2sku: always enabled (market optional). sku2raw/raw2raw: need a customer first.
	const isDisabled = !isRaw2Sku && !customerID

	// Load the full catalog upfront so "Select All" covers it; refetch when scope changes
	useEffect(() => {
		if (isDisabled) {
			setOptions([])
			return
		}
		const params = isRaw2Sku ? { all: 1 } : { customerID, limit: 500 }
		if (isRaw2Sku && markets?.length > 0) {
			params.markets = markets.map(m => m.value || m).join(',')
		}
		getSubmit(urlAjax, params)
			.then(res => { if (res) setOptions(res) })
			.catch(err => console.error('Error fetching sites:', err))
	}, [urlAjax, customerID, markets, isDisabled, isRaw2Sku])

	// Reset sites when the customer OR the meaning of the picker changes.
	//
	// `isCompetitorPicker` is in the deps because the two option sources are different
	// worlds: `/sites` returns the customer's OWN sites, `/competitor-sites` the others.
	// Keyed only on customerID + isRaw2Sku, switching sku2raw → raw2raw did not re-run
	// (customer unchanged, isRaw2Sku still false), so own-site ids survived into a
	// competitor-site field. valueObjects then dropped them for display — the field
	// rendered EMPTY with the "empty = all" placeholder — while handleCreate still sent
	// them, and with splitByTargetSites on the back built one batch per bogus target site.
	// Switching FROM raw2sku did clear them, which is what made the hole easy to miss.
	useEffect(() => {
		if (!isRaw2Sku && createBatchFormData.value.sites?.length > 0) {
			createBatchFormData.value = {...createBatchFormData.value, sites: []}
		}
	}, [customerID, isRaw2Sku, isCompetitorPicker])

	const marketLabel = markets?.length === 1
		? (markets[0].label || markets[0].value || markets[0])
		: markets?.length > 1 ? `${markets.length} markets` : null

	const placeholder = isRaw2Sku
		? (marketLabel ? `Search Sites in ${marketLabel}...` : "Search Sites...")
		: isCompetitorPicker
			? (customerID ? "Search competitor sites... (empty = all)" : "Select a Customer first")
			: (customerID ? "Search Sites..." : "Select a Customer first")

	// formData.sites stores raw IDs (schedule configs persist them verbatim and the
	// backend runner consumes them) — map to option objects only for display
	const valueObjects = (createBatchFormData.value.sites || [])
		.map(v => (v && typeof v === 'object') ? v : options.find(o => o.value === v))
		.filter(Boolean)

	return <SitesMultiSelect
		options={options}
		value={valueObjects}
		onChange={(newValue) => createBatchFormData.value = {
			...createBatchFormData.value,
			sites: newValue.map(o => o.value)
		}}
		label={isCompetitorPicker ? "Competitor Sites (optional)" : "Sites"}
		placeholder={placeholder}
		disabled={isDisabled}
		{...props}
	/>
}
